var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useGetList } from "react-admin";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tab, Table, TableBody, TableCell, TableRow, Tabs, TextField, Typography, } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataGrid } from "@mui/x-data-grid";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { IPermissions } from "@/types/keycloak";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { getGraphQLActionErrorReason } from "@/services/graphqlActionError";
import { ELECTORAL_LOG_CONSOLE_PAGE, ELECTORAL_LOG_CONSOLE_QUERY, ELECTORAL_LOG_CONSOLE_RECORD, ELECTORAL_LOG_CONSOLE_TENANTS, } from "@/queries/ElectoralLogConsole";
import { BALLOT_STATUSES, CONSOLE_TABLES, eventElections, FILTERS_BY_TABLE, PAGE_ORDERS, ROW_ID, columnField, displayValue, invalidFilters, isQueryError, tenantEvents, toConsoleFilters, toGridRows, } from "@/services/ElectoralLogConsole";
var PAGE_SIZES = [25, 50, 100, 200];
var DEFAULT_QUERY = "SELECT statement_kind, count(*) AS records\nFROM electoral_log_messages\nGROUP BY statement_kind\nORDER BY records DESC";
var GRID_HEIGHT = 620;
var MONOSPACE = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
var readHeaders = { headers: { "x-hasura-role": IPermissions.ELECTORAL_LOG_CONSOLE_READ } };
var queryHeaders = { headers: { "x-hasura-role": IPermissions.ELECTORAL_LOG_CONSOLE_QUERY } };
var gridColumns = function (_a) {
    var columns = _a.columns;
    return columns.map(function (column, index) { return ({
        field: columnField(index),
        headerName: column,
        minWidth: 120,
        flex: 1,
        sortable: false,
        valueFormatter: function (value) { return displayValue(value); },
    }); });
};
var FIRST_PAGE = { page: 0, after: null };
var FilterInput = function (_a) {
    var field = _a.field, value = _a.value, invalid = _a.invalid, elections = _a.elections, onChange = _a.onChange;
    var t = useTranslation().t;
    var aliasRenderer = useAliasRenderer();
    var label = t("electoralLogConsole.filters.".concat(field));
    if (field === "election_id" || field === "status") {
        var options = field === "status"
            ? BALLOT_STATUSES.map(function (status) { return ({
                value: status,
                label: t("electoralLogConsole.statuses.".concat(status)),
            }); })
            : elections.map(function (election) { return ({
                value: election.id,
                label: aliasRenderer(election),
            }); });
        return (_jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { id: "electoral-log-filter-".concat(field), children: label }), _jsxs(Select, { labelId: "electoral-log-filter-".concat(field), label: label, value: value, onChange: function (e) { return onChange(e.target.value); }, children: [_jsx(MenuItem, { value: "", children: t("electoralLogConsole.filters.any") }), options.map(function (option) { return (_jsx(MenuItem, { value: option.value, children: option.label }, option.value)); })] })] }));
    }
    var isDate = field === "created_after" || field === "created_before";
    return (_jsx(TextField, { size: "small", label: label, value: value, error: invalid, type: isDate ? "datetime-local" : "text", InputLabelProps: isDate ? { shrink: true } : undefined, onChange: function (e) { return onChange(e.target.value); }, fullWidth: true }));
};
var RecordDialog = function (_a) {
    var tenantId = _a.tenantId, electionEventId = _a.electionEventId, position = _a.position, onClose = _a.onClose;
    var t = useTranslation().t;
    var client = useApolloClient();
    var _b = useState(null), record = _b[0], setRecord = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    useEffect(function () {
        if (position === null) {
            return;
        }
        var cancelled = false;
        setRecord(null);
        setError(null);
        client
            .query({
            query: ELECTORAL_LOG_CONSOLE_RECORD,
            variables: { tenantId: tenantId, electionEventId: electionEventId, position: position },
            fetchPolicy: "no-cache",
            context: readHeaders,
        })
            .then(function (_a) {
            var _b;
            var data = _a.data;
            if (!cancelled) {
                setRecord((_b = data === null || data === void 0 ? void 0 : data.electoral_log_console_record) !== null && _b !== void 0 ? _b : null);
            }
        })
            .catch(function (failure) {
            var _a;
            if (!cancelled) {
                setError((_a = getGraphQLActionErrorReason(failure)) !== null && _a !== void 0 ? _a : t("electoralLogConsole.record.loadError"));
            }
        });
        return function () {
            cancelled = true;
        };
    }, [client, tenantId, electionEventId, position, t]);
    var fields = record
        ? Object.entries(record).filter(function (_a) {
            var key = _a[0];
            return key !== "message" && key !== "personal_data";
        })
        : [];
    return (_jsxs(Dialog, { open: position !== null, onClose: onClose, maxWidth: "md", fullWidth: true, children: [_jsx(DialogTitle, { children: t("electoralLogConsole.record.title", { position: position }) }), _jsxs(DialogContent, { dividers: true, children: [error && _jsx(Alert, { severity: "error", children: error }), (record === null || record === void 0 ? void 0 : record.personal_data) === "hidden" && (_jsx(Alert, { severity: "info", sx: { mb: 2 }, children: t("electoralLogConsole.personalDataHidden") })), record && (_jsxs(_Fragment, { children: [_jsx(Table, { size: "small", children: _jsx(TableBody, { children: fields.map(function (_a) {
                                        var key = _a[0], value = _a[1];
                                        return (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: { fontWeight: 600, width: 160 }, children: key }), _jsx(TableCell, { sx: { fontFamily: MONOSPACE, wordBreak: "break-all" }, children: displayValue(value) })] }, key));
                                    }) }) }), _jsx(Typography, { variant: "subtitle2", sx: { mt: 2, mb: 1 }, children: t("electoralLogConsole.record.message") }), _jsx(Box, { component: "pre", sx: {
                                    fontFamily: MONOSPACE,
                                    fontSize: 12,
                                    bgcolor: "grey.100",
                                    p: 2,
                                    borderRadius: 1,
                                    maxHeight: 360,
                                    overflow: "auto",
                                    m: 0,
                                }, children: JSON.stringify(record.message, null, 2) })] }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { disabled: !record, onClick: function () { var _a; return (_a = navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(JSON.stringify(record, null, 2)); }, children: t("electoralLogConsole.record.copy") }), _jsx(Button, { onClick: onClose, children: t("electoralLogConsole.record.close") })] })] }));
};
// Users of the super-admin tenant choose the tenant to browse; others browse their
// own, whose events and elections Hasura lists.
var TablesTab = function (_a) {
    var _b, _c, _d, _e;
    var superAdmin = _a.superAdmin;
    var t = useTranslation().t;
    var client = useApolloClient();
    var ownTenant = useTenantStore()[0];
    var aliasRenderer = useAliasRenderer();
    var _f = useState(""), chosenTenant = _f[0], setChosenTenant = _f[1];
    var _g = useQuery(ELECTORAL_LOG_CONSOLE_TENANTS, {
        skip: !superAdmin,
        fetchPolicy: "network-only",
        context: readHeaders,
    }), tenantsData = _g.data, tenantsError = _g.error;
    var tenants = (_c = (_b = tenantsData === null || tenantsData === void 0 ? void 0 : tenantsData.electoral_log_console_tenants) === null || _b === void 0 ? void 0 : _b.tenants) !== null && _c !== void 0 ? _c : [];
    var tenantId = (superAdmin && chosenTenant) || ownTenant || "";
    var ownEvents = useGetList("sequent_backend_election_event", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "created_at", order: "DESC" },
        filter: { tenant_id: tenantId },
    }, { enabled: !superAdmin && !!tenantId }).data;
    var events = superAdmin
        ? tenantEvents(tenants, tenantId)
        : (ownEvents !== null && ownEvents !== void 0 ? ownEvents : []);
    var _h = useState(""), eventId = _h[0], setEventId = _h[1];
    var ownElections = useGetList("sequent_backend_election", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "created_at", order: "ASC" },
        filter: { tenant_id: tenantId, election_event_id: eventId },
    }, { enabled: !superAdmin && !!tenantId && !!eventId }).data;
    var elections = superAdmin
        ? eventElections(tenants, tenantId, eventId)
        : (ownElections !== null && ownElections !== void 0 ? ownElections : []);
    var requestTenant = superAdmin ? tenantId : undefined;
    var _j = useState("records"), table = _j[0], setTable = _j[1];
    var _k = useState("newest-first"), order = _k[0], setOrder = _k[1];
    var _l = useState({}), draft = _l[0], setDraft = _l[1];
    var _m = useState({}), filters = _m[0], setFilters = _m[1];
    var _o = useState([]), invalid = _o[0], setInvalid = _o[1];
    var _p = useState(PAGE_SIZES[0]), pageSize = _p[0], setPageSize = _p[1];
    var _q = useState(FIRST_PAGE), position = _q[0], setPosition = _q[1];
    // Cursor each page starts after, as the pages read so far tell.
    var cursors = useRef([null]);
    var _r = useState(null), page = _r[0], setPage = _r[1];
    var _s = useState(false), loading = _s[0], setLoading = _s[1];
    var _t = useState(null), error = _t[0], setError = _t[1];
    var _u = useState(null), recordPosition = _u[0], setRecordPosition = _u[1];
    useEffect(function () {
        if (!eventId && (events === null || events === void 0 ? void 0 : events.length)) {
            setEventId(events[0].id);
        }
    }, [events, eventId]);
    var restart = function () {
        cursors.current = [null];
        setPosition(FIRST_PAGE);
    };
    // An event and its filters belong to one tenant: start over when the tenant changes,
    // here or in the tenant switcher, before any request pairs it with the old event.
    var _v = useState(tenantId), eventsTenant = _v[0], setEventsTenant = _v[1];
    if (eventsTenant !== tenantId) {
        setEventsTenant(tenantId);
        setEventId("");
        setDraft({});
        setFilters({});
        setInvalid([]);
        setRecordPosition(null);
        restart();
    }
    useEffect(function () {
        if (!eventId) {
            setPage(null);
            setError(null);
            setLoading(false);
            return;
        }
        var cancelled = false;
        setLoading(true);
        setError(null);
        client
            .query({
            query: ELECTORAL_LOG_CONSOLE_PAGE,
            variables: {
                tenantId: requestTenant,
                electionEventId: eventId,
                table: table,
                filters: toConsoleFilters(table, filters),
                order: order,
                after: position.after,
                limit: pageSize,
            },
            fetchPolicy: "no-cache",
            context: readHeaders,
        })
            .then(function (_a) {
            var data = _a.data;
            if (cancelled) {
                return;
            }
            var result = data === null || data === void 0 ? void 0 : data.electoral_log_console_page;
            cursors.current = cursors.current.slice(0, position.page + 1);
            cursors.current[position.page + 1] = result.next;
            setPage(result);
        })
            .catch(function (failure) {
            var _a;
            if (!cancelled) {
                setPage(null);
                setError((_a = getGraphQLActionErrorReason(failure)) !== null && _a !== void 0 ? _a : t("electoralLogConsole.loadError"));
            }
        })
            .finally(function () {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return function () {
            cancelled = true;
        };
    }, [client, requestTenant, eventId, table, order, filters, position, pageSize, t]);
    var changePagination = function (model) {
        if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            restart();
            return;
        }
        var after = cursors.current[model.page];
        if (after !== undefined) {
            setPosition({ page: model.page, after: after });
        }
    };
    var applyFilters = function () {
        var wrong = invalidFilters(table, draft);
        setInvalid(wrong);
        if (wrong.length === 0) {
            setFilters(__assign({}, draft));
            restart();
        }
    };
    var clearFilters = function () {
        setDraft({});
        setInvalid([]);
        setFilters({});
        restart();
    };
    var positionIndex = (_d = page === null || page === void 0 ? void 0 : page.columns.indexOf("position")) !== null && _d !== void 0 ? _d : -1;
    var columns = useMemo(function () {
        if (!page) {
            return [];
        }
        var data = gridColumns(page);
        if (table !== "records" || positionIndex < 0) {
            return data;
        }
        var view = {
            field: "__view",
            headerName: "",
            width: 56,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: function (params) { return (_jsx(IconButton, { size: "small", "aria-label": t("electoralLogConsole.record.view"), onClick: function () {
                    return setRecordPosition(Number(params.row[columnField(positionIndex)]));
                }, children: _jsx(VisibilityIcon, { fontSize: "small" }) })); },
        };
        return __spreadArray(__spreadArray([], data, true), [view], false);
    }, [page, table, positionIndex, t]);
    var rows = useMemo(function () { return (page ? toGridRows(page, position.page * pageSize) : []); }, [page, position.page, pageSize]);
    return (_jsxs(Stack, { spacing: 2, children: [tenantsError && (_jsx(Alert, { severity: "error", children: (_e = getGraphQLActionErrorReason(tenantsError)) !== null && _e !== void 0 ? _e : t("electoralLogConsole.loadError") })), _jsxs(Stack, { direction: { xs: "column", md: "row" }, spacing: 2, children: [superAdmin && (_jsxs(FormControl, { size: "small", sx: { minWidth: 220 }, children: [_jsx(InputLabel, { id: "electoral-log-tenant", children: t("electoralLogConsole.tenant") }), _jsx(Select, { labelId: "electoral-log-tenant", label: t("electoralLogConsole.tenant"), value: tenants.some(function (tenant) { return tenant.id === tenantId; }) ? tenantId : "", onChange: function (e) { return setChosenTenant(e.target.value); }, children: tenants.map(function (tenant) { return (_jsx(MenuItem, { value: tenant.id, children: tenant.slug }, tenant.id)); }) })] })), _jsxs(FormControl, { size: "small", sx: { minWidth: 280 }, children: [_jsx(InputLabel, { id: "electoral-log-event", children: t("electoralLogConsole.electionEvent") }), _jsx(Select, { labelId: "electoral-log-event", label: t("electoralLogConsole.electionEvent"), value: eventId, onChange: function (e) {
                                    setEventId(e.target.value);
                                    setDraft({});
                                    setFilters({});
                                    restart();
                                }, children: events.map(function (event) { return (_jsx(MenuItem, { value: event.id, children: aliasRenderer(event) }, event.id)); }) })] }), _jsxs(FormControl, { size: "small", sx: { minWidth: 200 }, children: [_jsx(InputLabel, { id: "electoral-log-table", children: t("electoralLogConsole.table") }), _jsx(Select, { labelId: "electoral-log-table", label: t("electoralLogConsole.table"), value: table, onChange: function (e) {
                                    setTable(e.target.value);
                                    setInvalid([]);
                                    restart();
                                }, children: CONSOLE_TABLES.map(function (name) { return (_jsx(MenuItem, { value: name, children: t("electoralLogConsole.tables.".concat(name)) }, name)); }) })] }), _jsxs(FormControl, { size: "small", sx: { minWidth: 180 }, children: [_jsx(InputLabel, { id: "electoral-log-order", children: t("electoralLogConsole.order.label") }), _jsx(Select, { labelId: "electoral-log-order", label: t("electoralLogConsole.order.label"), value: order, onChange: function (e) {
                                    setOrder(e.target.value);
                                    restart();
                                }, children: PAGE_ORDERS.map(function (name) { return (_jsx(MenuItem, { value: name, children: t("electoralLogConsole.order.".concat(name)) }, name)); }) })] })] }), _jsx(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(220px, 1fr))" },
                    gap: 2,
                    alignItems: "center",
                }, children: FILTERS_BY_TABLE[table].map(function (field) {
                    var _a;
                    return (_jsx(FilterInput, { field: field, value: (_a = draft[field]) !== null && _a !== void 0 ? _a : "", invalid: invalid.includes(field), elections: elections, onChange: function (value) {
                            var _a;
                            return setDraft(__assign(__assign({}, draft), (_a = {}, _a[field] = value, _a)));
                        } }, field));
                }) }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Button, { variant: "contained", onClick: applyFilters, children: t("electoralLogConsole.filters.apply") }), _jsx(Button, { onClick: clearFilters, children: t("electoralLogConsole.filters.clear") })] }), invalid.length > 0 && (_jsx(Alert, { severity: "warning", children: t("electoralLogConsole.filters.invalid") })), error && _jsx(Alert, { severity: "error", children: error }), page && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("electoralLogConsole.estimatedRows", {
                    rows: page.estimated_rows.toLocaleString(),
                }) })), (page === null || page === void 0 ? void 0 : page.personal_data) === "hidden" && page.personal_columns.length > 0 && (_jsx(Alert, { severity: "info", children: t("electoralLogConsole.personalDataHidden") })), _jsx(Box, { sx: { height: GRID_HEIGHT, width: "100%" }, children: _jsx(DataGrid, { rows: rows, columns: columns, getRowId: function (row) { return row[ROW_ID]; }, loading: loading, paginationMode: "server", rowCount: -1, paginationMeta: { hasNextPage: !!(page === null || page === void 0 ? void 0 : page.next) }, paginationModel: { page: position.page, pageSize: pageSize }, onPaginationModelChange: changePagination, pageSizeOptions: PAGE_SIZES, disableColumnFilter: true, disableColumnSorting: true, disableRowSelectionOnClick: true, showToolbar: true, slotProps: {
                        toolbar: {
                            showQuickFilter: false,
                            csvOptions: { fileName: "electoral-log-".concat(table) },
                            printOptions: { disableToolbarButton: true },
                        },
                    } }) }), _jsx(RecordDialog, { tenantId: requestTenant, electionEventId: eventId, position: recordPosition, onClose: function () { return setRecordPosition(null); } })] }));
};
// Each election event has a database of its own: a query reads the chosen event's.
var QueryTab = function () {
    var _a, _b, _c;
    var t = useTranslation().t;
    var client = useApolloClient();
    var aliasRenderer = useAliasRenderer();
    var _d = useState(DEFAULT_QUERY), sql = _d[0], setSql = _d[1];
    var _e = useState(null), result = _e[0], setResult = _e[1];
    var _f = useState(null), error = _f[0], setError = _f[1];
    var _g = useState(false), running = _g[0], setRunning = _g[1];
    var _h = useState(""), tenantId = _h[0], setTenantId = _h[1];
    var _j = useState(""), eventId = _j[0], setEventId = _j[1];
    var _k = useQuery(ELECTORAL_LOG_CONSOLE_TENANTS, {
        fetchPolicy: "network-only",
        context: readHeaders,
    }), tenantsData = _k.data, tenantsError = _k.error;
    var tenants = (_b = (_a = tenantsData === null || tenantsData === void 0 ? void 0 : tenantsData.electoral_log_console_tenants) === null || _a === void 0 ? void 0 : _a.tenants) !== null && _b !== void 0 ? _b : [];
    var events = tenantEvents(tenants, tenantId);
    var ready = !!tenantId && !!eventId && !!sql.trim();
    // Results belong to the event that produced them.
    var chooseEvent = function (tenant, event) {
        setTenantId(tenant);
        setEventId(event);
        setResult(null);
        setError(null);
    };
    var run = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, answer, failure_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (running || !ready) {
                        return [2 /*return*/];
                    }
                    setRunning(true);
                    setError(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, client.query({
                            query: ELECTORAL_LOG_CONSOLE_QUERY,
                            variables: { tenantId: tenantId, electionEventId: eventId, sql: sql },
                            fetchPolicy: "no-cache",
                            context: queryHeaders,
                        })];
                case 2:
                    data = (_b.sent()).data;
                    answer = data === null || data === void 0 ? void 0 : data.electoral_log_console_query;
                    if (isQueryError(answer)) {
                        setResult(null);
                        setError(answer.error);
                    }
                    else {
                        setResult(answer);
                    }
                    return [3 /*break*/, 5];
                case 3:
                    failure_1 = _b.sent();
                    setResult(null);
                    setError((_a = getGraphQLActionErrorReason(failure_1)) !== null && _a !== void 0 ? _a : t("electoralLogConsole.query.error"));
                    return [3 /*break*/, 5];
                case 4:
                    setRunning(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var columns = useMemo(function () { return (result ? gridColumns(result) : []); }, [result]);
    var rows = useMemo(function () { return (result ? toGridRows(result) : []); }, [result]);
    return (_jsxs(Stack, { spacing: 2, children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [t("electoralLogConsole.query.help"), " ", t("electoralLogConsole.query.scope")] }), tenantsError && (_jsx(Alert, { severity: "error", children: (_c = getGraphQLActionErrorReason(tenantsError)) !== null && _c !== void 0 ? _c : t("electoralLogConsole.loadError") })), _jsxs(Stack, { direction: { xs: "column", md: "row" }, spacing: 2, children: [_jsxs(FormControl, { size: "small", sx: { minWidth: 220 }, children: [_jsx(InputLabel, { id: "electoral-log-query-tenant", children: t("electoralLogConsole.tenant") }), _jsx(Select, { labelId: "electoral-log-query-tenant", label: t("electoralLogConsole.tenant"), value: tenants.some(function (tenant) { return tenant.id === tenantId; }) ? tenantId : "", disabled: running, onChange: function (e) { return chooseEvent(e.target.value, ""); }, children: tenants.map(function (tenant) { return (_jsx(MenuItem, { value: tenant.id, children: tenant.slug }, tenant.id)); }) })] }), _jsxs(FormControl, { size: "small", sx: { minWidth: 280 }, children: [_jsx(InputLabel, { id: "electoral-log-query-event", children: t("electoralLogConsole.electionEvent") }), _jsx(Select, { labelId: "electoral-log-query-event", label: t("electoralLogConsole.electionEvent"), value: events.some(function (event) { return event.id === eventId; }) ? eventId : "", disabled: running, onChange: function (e) { return chooseEvent(tenantId, e.target.value); }, children: events.map(function (event) { return (_jsx(MenuItem, { value: event.id, children: aliasRenderer(event) }, event.id)); }) })] })] }), !eventId && (_jsx(Alert, { severity: "info", children: t("electoralLogConsole.query.chooseEvent") })), _jsx(TextField, { multiline: true, minRows: 6, fullWidth: true, value: sql, placeholder: t("electoralLogConsole.query.placeholder"), onChange: function (e) { return setSql(e.target.value); }, onKeyDown: function (e) {
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                        run();
                    }
                }, inputProps: { spellCheck: false, style: { fontFamily: MONOSPACE, fontSize: 13 } } }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", justifyContent: "space-between", children: [_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("electoralLogConsole.query.limits") }), _jsx(Button, { variant: "contained", onClick: run, disabled: running || !ready, children: t("electoralLogConsole.query.run") })] }), error && (_jsx(Alert, { severity: "error", sx: { whiteSpace: "pre-wrap", fontFamily: MONOSPACE }, children: error })), result && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("electoralLogConsole.query.summary", {
                            rows: result.rows.length.toLocaleString(),
                            ms: result.elapsed_ms.toLocaleString(),
                        }) }), result.truncated && (_jsx(Alert, { severity: "info", children: t("electoralLogConsole.query.truncated", {
                            rows: result.rows.length.toLocaleString(),
                        }) })), _jsx(Box, { sx: { height: GRID_HEIGHT, width: "100%" }, children: _jsx(DataGrid, { rows: rows, columns: columns, getRowId: function (row) { return row[ROW_ID]; }, loading: running, initialState: { pagination: { paginationModel: { pageSize: 25 } } }, pageSizeOptions: [25, 50, 100], disableRowSelectionOnClick: true, showToolbar: true, slotProps: {
                                toolbar: {
                                    showQuickFilter: false,
                                    csvOptions: { fileName: "electoral-log-query" },
                                    printOptions: { disableToolbarButton: true },
                                },
                            } }) })] }))] }));
};
export var ElectoralLogConsole = function () {
    var t = useTranslation().t;
    var authContext = useContext(AuthContext);
    var _a = useState("tables"), tab = _a[0], setTab = _a[1];
    var allowed = function (permission) {
        return authContext.isAuthorized(true, authContext.tenantId, permission);
    };
    // Only users of the super-admin tenant browse other tenants' events or query an
    // event's electoral-log database.
    var superAdminAllowed = function (permission) {
        return authContext.isAuthorized(true, null, permission);
    };
    var canRead = allowed(IPermissions.ELECTORAL_LOG_CONSOLE_READ);
    var superAdmin = superAdminAllowed(IPermissions.ELECTORAL_LOG_CONSOLE_READ);
    var canQuery = superAdminAllowed(IPermissions.ELECTORAL_LOG_CONSOLE_QUERY) &&
        superAdminAllowed(IPermissions.ELECTORAL_LOG_PERSONAL_DATA_READ);
    if (!canRead) {
        return _jsx(Alert, { severity: "warning", children: t("electoralLogConsole.notAllowed") });
    }
    return (
    // The page takes the width the layout gives it, whatever its tables' width.
    _jsx(Card, { sx: { contain: "inline-size" }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h4", sx: { mb: 1 }, children: t("electoralLogConsole.title") }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: t("electoralLogConsole.subtitle") }), _jsxs(Tabs, { value: tab, onChange: function (_, value) { return setTab(value); }, sx: { mb: 2 }, children: [_jsx(Tab, { value: "tables", label: t("electoralLogConsole.tabs.tables") }), canQuery && _jsx(Tab, { value: "query", label: t("electoralLogConsole.tabs.query") })] }), tab === "query" && canQuery ? _jsx(QueryTab, {}) : _jsx(TablesTab, { superAdmin: superAdmin })] }) }));
};
